import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import { Header, Label } from 'common/components/texts/Texts';
import { useTranslation } from 'react-i18next';
import Button from 'common/components/button/Button';
import TextInput from 'common/components/textInput/TextInput';
import SelectInput from 'common/components/selectInput/SelectInput';
import { Row, Col } from 'react-bootstrap';
import { ParameterSectionParameterType } from 'common/components/parametersSections/models';
import { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';
import CatalogParametersService from 'api/catalogParameters/CatalogParametersService';
import { UnitDto } from 'api/units/models/UnitDto';
import UnitsService from 'api/units/UnitsService';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import InputError from 'common/components/inputError/InputError';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';

type Props = {
    onClose: () => void;
    onCreated: (param: CatalogParameterDto) => void;
} & ReactModal.Props;

const CreateCatalogParameterModal: React.FC<Props> = ({ onClose, onCreated, ...props }: Props) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [parameterType, setParameterType] = useState('');
    const [unitId, setUnitId] = useState<string | null>(null);
    const [formulaExpression, setFormulaExpression] = useState<string | null>(null);
    const [units, setUnits] = useState<UnitDto[]>([]);
    const [allCatalogParams, setAllCatalogParams] = useState<CatalogParameterDto[]>([]);
    const [submitted, setSubmitted] = useState(false);

    const parameterTypeOptions = Object.keys(ParameterSectionParameterType)
        .filter(key => key !== ParameterSectionParameterType.NUMBER_MIN_MAX)
        .map(key => ({
            label: t('common.parameters_sections.parameters_types.' + key),
            value: key,
        })).sort((a, b) => a.label.localeCompare(b.label));

    const unitOptions = units.map(u => ({
        label: `${u.name} (${u.symbol})`,
        value: u.id,
    }));

    useEffect(() => {
        if (props.isOpen) {
            setName('');
            setParameterType('');
            setUnitId(null);
            setFormulaExpression(null);
            setSubmitted(false);
            void UnitsService.getCatalog().then(setUnits);
            void CatalogParametersService.getCatalog().then(setAllCatalogParams);
        }
    }, [props.isOpen]);

    const getFormulaParams = (): ExpressionParameter[] => {
        const numericTypes = ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'];
        const filtered = allCatalogParams
            .filter(cp => cp.parameterType && numericTypes.includes(cp.parameterType));

        return filtered.map(cp => ({
            id: ExpressionSystem.formatParameterId([cp.id]),
            name: ExpressionSystem.formatParameterName([cp.name]),
            value: 0,
        }));
    };

    const onSave = async () => {
        setSubmitted(true);
        if (!name.trim() || !parameterType) return;

        try {
            Loading.show();
            const model: CatalogParameterDto = {
                id: '',
                name: name.trim(),
                parameterType,
                unitId,
                formulaExpression,
            };
            const newId = await CatalogParametersService.create(model);
            const created = await CatalogParametersService.getById(newId);
            Toast.success(t('messages.record_save_success'));
            onCreated(created);
        } catch (error: any) {
            const msg = error?.response?.data;
            if (typeof msg === 'string' && msg.includes('CATALOG_PARAMETER_NAME_ALREADY_EXISTS')) {
                Toast.error(t('catalog_parameters.errors.name_already_exists'));
            } else {
                Toast.error(t('messages.record_save_error'));
            }
        } finally {
            Loading.hide();
        }
    };

    return (
        <Modal {...props} style={{ content: { minWidth: 500 } }} onRequestClose={onClose}>
            <div>
                <ModalHeader>
                    <Header>{t('common.new')} {t('catalog_parameters.catalog_parameter')}</Header>
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col xs={12} className='mb-3'>
                            <Label space>{t('catalog_parameters.list.name')}*</Label>
                            <TextInput
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                hasError={submitted && !name.trim()}
                            />
                            {submitted && !name.trim() && <InputError error={{ type: 'required' }} />}
                        </Col>
                        <Col xs={6}>
                            <Label space>{t('catalog_parameters.list.parameter_type')}*</Label>
                            <SelectInput
                                options={parameterTypeOptions}
                                value={parameterType}
                                onChange={(v) => setParameterType(v ?? '')}
                                hasError={submitted && !parameterType}
                            />
                            {submitted && !parameterType && <InputError error={{ type: 'required' }} />}
                        </Col>
                        <Col xs={6}>
                            <Label space>{t('catalog_parameters.list.unit')}</Label>
                            <SelectInput
                                options={unitOptions}
                                value={unitId ?? ''}
                                onChange={(v) => setUnitId(v || null)}
                            />
                        </Col>
                        <Col xs={12} className='mt-3'>
                            <Label space>{t('catalog_parameters.list.formula')}</Label>
                            <TextInput
                                value={ExpressionSystem.formatExpressionForDisplay(
                                    formulaExpression ?? '',
                                    getFormulaParams()
                                )}
                                onChange={() => {}}
                                placeholder={t('common.parameters_sections.formula_placeholder')}
                                readOnly={true}
                                style={{ cursor: 'default' }}
                                expressionButton={{
                                    value: formulaExpression ?? '',
                                    title: name || t('catalog_parameters.list.formula'),
                                    loadParameters: getFormulaParams,
                                    onChange: (expr: string | null) => setFormulaExpression(expr),
                                }}
                            />
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter className="text-right">
                    <Button fw variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
                    <Button onClick={() => { void onSave(); }}>{t('common.save')}</Button>
                </ModalFooter>
            </div>
        </Modal>
    );
};

export default CreateCatalogParameterModal;
